import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription, of } from 'rxjs';
import { catchError, repeat } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { MainState } from '../main/main.state';

interface CacheProgressResponse {
  inProgress: boolean;
  complete: boolean;
  stage: 'downloading' | 'reading-metadata' | 'generating-previews' | 'complete' | null;
  bytesCached: number;
  totalBytes: number;
  slicesCached: number;
  totalSlices: number;
  percent: number;
}

// Polls jit-service `/api/cache-progress` while a large file is being prepared
// for display. The backend reports two phases — downloading (GCS → PVC) and
// generating-previews (per-slice PNG generation). The service drives
// `cacheProgress$` for the progress bar value AND updates the image-loading
// message so the user sees one continuous "we're working on it" overlay from
// click through to the rendered image, with a label that matches what's
// actually happening.
@Injectable({ providedIn: 'root' })
export class CacheProgressService {
  private readonly url = environment.slideCropServer;
  private readonly pollIntervalMs = 500;

  private sub?: Subscription;
  private lastStage: string | null = null;

  constructor(private http: HttpClient, private mainState: MainState) {
    // Defensive: if image loading ever flips to false (success path, cancel,
    // or anywhere else that resets loading state), make sure we stop polling
    // and clear the progress so the overlay returns to its default state.
    this.mainState.isImageLoading$().subscribe(isLoading => {
      if (!isLoading && this.sub) this.stop();
    });
  }

  start(rawFile: any): void {
    this.stop();
    if (!rawFile) return;

    const info = btoa(JSON.stringify(rawFile));
    const endpoint = `${this.url}cache-progress?info=${info}`;
    this.lastStage = null;

    // Poll until the backend reports complete or the constructor's
    // imageLoading=false subscription stops us (success / error / cancel).
    // We do not give up on "idle" responses: a large file's cache fill may
    // not begin until /api/preview is issued, which can be several seconds
    // after the click that started polling.
    //
    // Poll SEQUENTIALLY (request -> wait for it -> delay -> repeat), not on a
    // fixed timer with switchMap. When the backend is busy (a multi-GB file's
    // Bio-Formats parse can make each /cache-progress response take >500ms), a
    // fixed-interval switchMap aborts the in-flight request every tick — that's
    // the NS_BINDING_ABORTED storm in the network panel, and it also froze the
    // bar because the handler rarely received a value. repeat({ delay }) only
    // re-fires after the previous response settles, so requests never overlap.
    this.sub = this.http.get<CacheProgressResponse>(endpoint).pipe(
      catchError(() => of(null)),
      repeat({ delay: this.pollIntervalMs }),
    ).subscribe(p => {
      if (!p) return;
      // Once the download stage is past (any later stage, or a completed
      // stage), the file is on the cache disk. Mark the image cached so a
      // subsequent zoom re-fetch shows only a spinner instead of the
      // "Caching image..." message — the file is already local, nothing is
      // being cached. (imageCached starts from metadata.zoomStatus.cached at
      // click time, which is false for a file that wasn't cached yet and was
      // never updated once caching finished.)
      if (p.complete || (p.inProgress && p.stage && p.stage !== 'downloading')) {
        this.mainState.setImageCached(true);
      }
      if (p.inProgress) {
        this.applyStageMessage(p.stage);
        this.mainState.setCacheProgress(p.percent);
      } else if (p.complete) {
        // A stage just completed. Don't stop polling here — the backend
        // pipeline is multi-stage: download → preview generation → done,
        // and each intermediate stage briefly reports complete=true between
        // its own complete() call and the next stage's startPreviewGen()
        // call. Stopping on the first complete signal made the bar vanish
        // right when preview-gen was about to start. Polling auto-stops via
        // the imageLoading$ subscription in the constructor once the
        // overlay is actually dismissed.
        this.applyStageMessage(p.stage);
        this.mainState.setCacheProgress(100);
      }
    });
  }

  stop(): void {
    this.sub?.unsubscribe();
    this.sub = undefined;
    this.mainState.setCacheProgress(null);
    this.lastStage = null;
  }

  private applyStageMessage(stage: string | null): void {
    if (stage === this.lastStage) return;
    this.lastStage = stage;
    if (stage === 'downloading') {
      this.mainState.setImageLoadingMessage('Caching image...');
    } else if (stage === 'reading-metadata') {
      // Bio-Formats IFD walk of the cached file — visible to the user as
      // a brief 100%-bar pause between the download finishing and the
      // preview-gen starting. Labeling it so they know we're not idle.
      this.mainState.setImageLoadingMessage('Reading image metadata...');
    } else if (stage === 'generating-previews') {
      this.mainState.setImageLoadingMessage('Generating preview...');
    }
    // stage === 'complete' or null: leave the existing message alone; the
    // overlay is about to disappear anyway.
  }
}
